import { Box, IconButton, Typography } from "@mui/material"
import { hasAmendsContent } from "authoring/routes/project/amends/lib/amends-content"
import { buildContentPreview, hasLongContent } from "authoring/routes/project/amends/lib/amends-content-preview"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { If } from "lib/@components/switch"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { htmlToText } from "lib/html-to-text"
import { RejectionDecisionCounts } from "./rejection-decision-counts"
import { RejectionDialog } from "./rejection-dialog"

export function DraftRejectionComment({ rejection, approvedCount = 0, rejectedCount = 0 }) {
    const openRejectionDialog = useDialog(<RejectionDialog />, { maxWidth: "sm", fullScreen: false })
    if (!rejection) return null

    const content = rejection?.payload?.content
    const dateLabel = rejection?.date ? new Date(rejection.date).format(DATE_FORMATS.long) : ""
    const hasContent = hasAmendsContent(content)
    const textContent = hasContent ? htmlToText(content, true).trim() : ""
    const isLongContent = hasContent && hasLongContent(content)
    const previewContent = hasContent ? buildContentPreview(content) : ""
    const displayContent = isLongContent ? previewContent : textContent

    return (
        <Box sx={{ my: 2, display: "flex", minWidth: "100%" }} data-cy="authoring-draft-rejection-banner">
            <Box sx={{ m: 1, mr: 2 }}>
                <UserAvatar sx={{ width: 32, height: 32 }} user={{ displayName: rejection?.userName }} />
            </Box>
            <Box sx={{ minWidth: "80%" }}>
                <Box display="flex" alignItems="center" sx={{ my: 1 }}>
                    <TruncatedTypography
                        variant="caption"
                        sx={{ color: "text.secondary", maxWidth: "fit-content", flex: 1 }}
                    >
                        {rejection?.userName}
                    </TruncatedTypography>
                    <Typography variant={"caption"} sx={{ color: "text.secondary", flexShrink: 0 }}>
                        , {dateLabel}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        bgcolor: "background.neutral",
                        borderRadius: 2,
                        p: 2,
                    }}
                >
                    <Box display="flex" alignItems="center" sx={{ mb: hasContent ? 0.5 : 0 }}>
                        <Typography variant="subtitle1" sx={{ flex: 1 }}>
                            Draft Rejected
                        </Typography>
                        <IconButton
                            aria-label="View rejection details"
                            color="primary"
                            size="small"
                            onClick={showRejectionDialog}
                            data-cy="rejection-comment-view-details"
                        >
                            <Iconify icon="ic:outline-remove-red-eye" />
                        </IconButton>
                    </Box>
                    <If truthy={hasContent}>
                        <Typography
                            data-cy="rejection-comment-preview"
                            variant="body2"
                            sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            {displayContent}
                        </Typography>
                    </If>
                    <RejectionDecisionCounts approvedCount={approvedCount} rejectedCount={rejectedCount} />
                </Box>
            </Box>
        </Box>
    )

    async function showRejectionDialog() {
        await openRejectionDialog({
            content,
            approvedCount,
            rejectedCount,
        })
    }
}
